import { useEffect, useMemo, useState, type KeyboardEvent, type PointerEvent } from 'react';
import type { BusinessDay } from '../business-types';
import { EmptyState } from '../components';

export interface BusinessTrendProps {
  days: BusinessDay[];
  mode: 'balance' | 'cashflow';
  compact?: boolean;
}

type ChartDay = { day: BusinessDay; time: number; sourceIndex: number };
type Point = { x: number; y: number };

const HEIGHT = 300;
const COMPACT_HEIGHT = 130;
const RIGHT = 8;
const TOP = 10;
const BOTTOM = 24;

const exactMoney = new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: 'always' });
const compactMoney = new Intl.NumberFormat('es-ES', { notation: 'compact', maximumFractionDigits: 1 });
export const dayLabel = new Intl.DateTimeFormat('es-ES', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
export const shortDayLabel = new Intl.DateTimeFormat('es-ES', { day: '2-digit', month: 'short', timeZone: 'UTC' });
const observationLabel = new Intl.DateTimeFormat('es-ES', { dateStyle: 'medium', timeStyle: 'short' });

/** Draw at the real pixel width so axis text stays one size instead of scaling with the viewBox. */
export function useWidth(): [(node: HTMLDivElement | null) => void, number] {
  const [node, setNode] = useState<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(640);
  useEffect(() => {
    if (!node) return;
    const observer = new ResizeObserver(([entry]) => { if (entry.contentRect.width > 0) setWidth(entry.contentRect.width); });
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);
  return [setNode, width];
}

export function BusinessTrend({ days, mode, compact = false }: BusinessTrendProps) {
  const data = useMemo(() => prepareDays(days), [days]);
  const [selected, setSelected] = useState<number | null>(null);
  const [ref, width] = useWidth();
  const height = compact ? COMPACT_HEIGHT : HEIGHT;
  const left = compact ? 2 : 54;
  const bottom = BOTTOM;
  const plotWidth = width - left - RIGHT;
  const plotHeight = height - TOP - bottom;
  const title = mode === 'balance' ? 'Saldo observado' : 'Flujo acumulado';
  const values = data.map(({ day }) => mode === 'balance' ? finiteOrNull(day.closingBalance) : finiteOrNull(day.cumulativeNet));
  const known = values.filter((value): value is number => value !== null);

  if (data.length === 0 || known.length === 0) {
    return <EmptyState title={mode === 'balance' ? 'Sin observaciones de wallet' : 'Sin actividad en el periodo'} detail={mode === 'balance' ? ' No se reconstruyen valores para días sin captura.' : ''} />;
  }

  const [low, high] = valueDomain(known, mode === 'cashflow');
  const x = makeXScale(data, left, width - RIGHT);
  const y = (value: number) => TOP + ((high - value) / (high - low)) * plotHeight;
  const points: Array<Point | null> = values.map((value, index) => value === null ? null : { x: x(index), y: y(value) });
  const ticks = makeTicks(low, high, compact ? 2 : 5);
  const dateTicks = makeDateTicks(data.length, width < 420 ? 3 : compact ? 2 : 7);
  const active = boundedIndex(selected, data.length);
  const activeX = active === null ? null : x(active);
  const activePoint = active === null ? null : points[active];
  const fallback = mode === 'balance' ? lastKnownIndex(values) : data.length - 1;

  const selectFromPointer = (event: PointerEvent<SVGRectElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    if (bounds.width === 0) return;
    setSelected(nearestIndex(data, left + clamp((event.clientX - bounds.left) / bounds.width, 0, 1) * plotWidth, x));
  };

  return <figure className="chart" data-chart={mode}>
    <div className="chart-plot" ref={ref}>
      <svg height={height} viewBox={`0 0 ${width} ${height}`} role="img" tabIndex={0}
        aria-label={`${title}: ${known.length} observaciones reales en ${data.length} días, de ${formatDay(data[0])} a ${formatDay(data[data.length - 1])}. Los días sin observación quedan sin unir. Usa las flechas para consultar cada día.`}
        onKeyDown={(event) => { const next = keyboardIndex(event, active, data.length, fallback); if (next !== null) setSelected(next); }}
        onFocus={() => setSelected(active ?? fallback)} onBlur={() => setSelected(null)}>
        {ticks.map((tick, index) => <g className="chart-axis" key={index}>
          <line x1={left} x2={width - RIGHT} y1={y(tick)} y2={y(tick)} className={Math.abs(tick) < 1e-9 ? 'chart-zero' : undefined} />
          {!compact && <text x={left - 8} y={y(tick) + 4} textAnchor="end">{axisMoney(tick)}</text>}
        </g>)}
        {dateTicks.map((index) => <text key={index} x={x(index)} y={height - 7} textAnchor={index === 0 ? 'start' : index === data.length - 1 ? 'end' : 'middle'}>{formatShortDay(data[index])}</text>)}
        {splitSegments(points).map((segment, index) => <path key={index} className="chart-line" d={segment.map((point, at) => `${at ? 'L' : 'M'}${point.x.toFixed(1)} ${point.y.toFixed(1)}`).join('')} />)}
        {data.length <= 45 && points.map((point, index) => point && <circle key={index} className="chart-dot" cx={point.x} cy={point.y} r={2} />)}
        {activeX !== null && <line className="chart-cursor" x1={activeX} x2={activeX} y1={TOP} y2={height - bottom} />}
        {activePoint && <circle className="chart-focus" cx={activePoint.x} cy={activePoint.y} r={4} />}
        <rect className="chart-hit" x={left} y={TOP} width={Math.max(plotWidth, 0)} height={plotHeight} onPointerMove={selectFromPointer} onPointerDown={selectFromPointer} onPointerLeave={() => setSelected(null)} />
      </svg>
      {active !== null && activeX !== null && <Tooltip x={activeX} width={width} day={data[active].day} rows={[
        [title, money(values[active])],
        ['Flujo del día', money(finiteOrZero(data[active].day.netCashflow))],
        ...(compact ? [] : [['Ingresos', money(finiteOrZero(data[active].day.income))], ['Gastos', money(finiteOrZero(data[active].day.expenses))]]),
      ]} foot={mode === 'balance' ? (data[active].day.balanceObservedAt ? `Observado ${formatObservation(data[active].day.balanceObservedAt)}` : 'Sin observación ese día') : undefined} />}
    </div>
    {!compact && mode === 'balance' && <figcaption>{known.length} de {data.length} días con saldo observado. Los huecos no se interpolan.</figcaption>}
  </figure>;
}

export function BusinessCashBars({ days, labels = ['Ingresos', 'Gastos'] }: { days: BusinessDay[]; labels?: [string, string] }) {
  const data = useMemo(() => prepareDays(days), [days]);
  const [selected, setSelected] = useState<number | null>(null);
  const [ref, width] = useWidth();

  if (data.length === 0) return <EmptyState title="Sin actividad diaria" />;

  const left = 54;
  const incomes = data.map(({ day }) => finiteOrZero(day.income));
  const expenses = data.map(({ day }) => finiteOrZero(day.expenses));
  const [low, high] = valueDomain([...incomes, ...expenses], true);
  const height = 240;
  const plotWidth = width - left - RIGHT;
  const plotHeight = height - TOP - BOTTOM;
  // Inset so the outermost bars are not clipped by the plot edges.
  const inset = Math.min(16, plotWidth / Math.max(data.length, 1) / 2);
  const x = makeXScale(data, left + inset, width - RIGHT - inset);
  const y = (value: number) => TOP + ((high - value) / (high - low)) * plotHeight;
  const zeroY = y(clamp(0, low, high));
  const barWidth = Math.min(14, Math.max(1.5, (plotWidth / Math.max(data.length, 4)) * .34));
  const active = boundedIndex(selected, data.length);
  const activeX = active === null ? null : x(active);

  const selectFromPointer = (event: PointerEvent<SVGRectElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    if (bounds.width === 0) return;
    setSelected(nearestIndex(data, left + clamp((event.clientX - bounds.left) / bounds.width, 0, 1) * plotWidth, x));
  };

  return <figure className="chart" data-chart="income-expenses">
    <div className="chart-legend" aria-hidden="true"><span className="in">{labels[0]}</span><span className="out">{labels[1]}</span></div>
    <div className="chart-plot" ref={ref}>
      <svg height={height} viewBox={`0 0 ${width} ${height}`} role="img" tabIndex={0}
        aria-label={`${labels[0]} y ${labels[1].toLowerCase()} diarios durante ${data.length} días, de ${formatDay(data[0])} a ${formatDay(data[data.length - 1])}. Usa las flechas para consultar cada día.`}
        onKeyDown={(event) => { const next = keyboardIndex(event, active, data.length, data.length - 1); if (next !== null) setSelected(next); }}
        onFocus={() => setSelected(active ?? data.length - 1)} onBlur={() => setSelected(null)}>
        {makeTicks(low, high, 4).map((tick, index) => <g className="chart-axis" key={index}>
          <line x1={left} x2={width - RIGHT} y1={y(tick)} y2={y(tick)} className={Math.abs(tick) < 1e-9 ? 'chart-zero' : undefined} />
          <text x={left - 8} y={y(tick) + 4} textAnchor="end">{axisMoney(tick)}</text>
        </g>)}
        {makeDateTicks(data.length, width < 420 ? 3 : 7).map((index) => <text key={index} x={x(index)} y={height - 7} textAnchor={index === 0 ? 'start' : index === data.length - 1 ? 'end' : 'middle'}>{formatShortDay(data[index])}</text>)}
        {data.map((item, index) => <g key={`${item.day.date}-${item.sourceIndex}`}>
          {incomes[index] > 0 && <rect className="chart-bar chart-bar--in" x={x(index) - barWidth - .5} y={y(incomes[index])} width={barWidth} height={Math.max(zeroY - y(incomes[index]), 1)} />}
          {expenses[index] > 0 && <rect className="chart-bar chart-bar--out" x={x(index) + .5} y={y(expenses[index])} width={barWidth} height={Math.max(zeroY - y(expenses[index]), 1)} />}
        </g>)}
        {activeX !== null && <line className="chart-cursor" x1={activeX} x2={activeX} y1={TOP} y2={height - BOTTOM} />}
        <rect className="chart-hit" x={left} y={TOP} width={Math.max(plotWidth, 0)} height={plotHeight} onPointerMove={selectFromPointer} onPointerDown={selectFromPointer} onPointerLeave={() => setSelected(null)} />
      </svg>
      {active !== null && activeX !== null && <Tooltip x={activeX} width={width} day={data[active].day} rows={[
        [labels[0], money(incomes[active])],
        [labels[1], money(expenses[active])],
        ['Flujo neto', money(finiteOrZero(data[active].day.netCashflow))],
      ]} />}
    </div>
  </figure>;
}

function Tooltip({ day, rows, x, width, foot }: { day: BusinessDay; rows: string[][]; x: number; width: number; foot?: string }) {
  return <div className="chart-tip" style={{ left: clamp(x, Math.min(119, width / 2), Math.max(width - 119, width / 2)) }} role="status" aria-live="polite">
    <strong>{formatRawDay(day.date)}</strong>
    <dl>{rows.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}</dl>
    {foot && <small>{foot}</small>}
  </div>;
}

function prepareDays(days: BusinessDay[]): ChartDay[] {
  return days.map((day, sourceIndex) => ({ day, time: parseDay(day.date, sourceIndex), sourceIndex }))
    .sort((a, b) => a.time - b.time || a.sourceIndex - b.sourceIndex);
}

function parseDay(value: string, fallback: number) {
  const parsed = Date.parse(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00Z` : value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function makeXScale(data: ChartDay[], start: number, end: number) {
  const first = data[0]?.time ?? 0;
  const last = data[data.length - 1]?.time ?? first;
  return (index: number) => last === first ? (start + end) / 2 : start + ((data[index].time - first) / (last - first)) * (end - start);
}

export function valueDomain(values: number[], includeZero: boolean): [number, number] {
  const finite = values.filter(Number.isFinite);
  let low = finite.length ? Math.min(...finite) : 0;
  let high = finite.length ? Math.max(...finite) : 0;
  if (includeZero) {
    low = Math.min(low, 0);
    high = Math.max(high, 0);
  }
  if (low === high) {
    const spread = Math.max(Math.abs(low) * .08, 1);
    low -= spread;
    high += spread;
  } else {
    const padding = (high - low) * .08;
    if (!includeZero || low < 0) low -= padding;
    if (!includeZero || high > 0) high += padding;
  }
  return [low, high];
}

export function makeTicks(low: number, high: number, count: number) {
  return Array.from({ length: count }, (_, index) => low + ((high - low) * index) / (count - 1));
}

export function makeDateTicks(length: number, count: number) {
  if (length <= 1) return [0];
  const steps = Math.min(count, length);
  return [...new Set(Array.from({ length: steps }, (_, index) => Math.round((index * (length - 1)) / (steps - 1))))];
}

function splitSegments(points: Array<Point | null>) {
  const segments: Point[][] = [];
  let segment: Point[] = [];
  points.forEach((point) => {
    if (point) segment.push(point);
    else if (segment.length) {
      segments.push(segment);
      segment = [];
    }
  });
  if (segment.length) segments.push(segment);
  return segments;
}

function nearestIndex(data: ChartDay[], chartX: number, scale: (index: number) => number) {
  let nearest = 0;
  let distance = Number.POSITIVE_INFINITY;
  data.forEach((_, index) => {
    const next = Math.abs(scale(index) - chartX);
    if (next < distance) {
      distance = next;
      nearest = index;
    }
  });
  return nearest;
}

export function keyboardIndex(event: KeyboardEvent<SVGSVGElement>, selected: number | null, length: number, fallback: number) {
  if (length === 0 || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return null;
  event.preventDefault();
  if (event.key === 'Home') return 0;
  if (event.key === 'End') return length - 1;
  return clamp((selected ?? fallback) + (event.key === 'ArrowLeft' ? -1 : 1), 0, length - 1);
}

function boundedIndex(index: number | null, length: number) {
  if (index === null || length === 0 || !Number.isInteger(index)) return null;
  return clamp(index, 0, length - 1);
}

function lastKnownIndex(values: Array<number | null>) {
  for (let index = values.length - 1; index >= 0; index -= 1) if (values[index] !== null) return index;
  return values.length - 1;
}

function formatDay(item: ChartDay) { return dayLabel.format(new Date(item.time)); }
function formatShortDay(item: ChartDay) { return shortDayLabel.format(new Date(item.time)); }

function formatRawDay(value: string) {
  const parsed = Date.parse(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00Z` : value);
  return Number.isFinite(parsed) ? dayLabel.format(new Date(parsed)) : value;
}

function formatObservation(value: string) {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : observationLabel.format(parsed);
}

function money(value: number | null) {
  return value === null ? 'Sin observación' : `${exactMoney.format(value)} ISK`;
}

export function axisMoney(value: number) {
  return compactMoney.format(Math.abs(value) < 1e-9 ? 0 : value);
}

function finiteOrNull(value: number | null) {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function finiteOrZero(value: number) {
  return Number.isFinite(value) ? value : 0;
}

export function clamp(value: number, low: number, high: number) {
  return Math.min(high, Math.max(low, value));
}
